/**
 * Client queue for segmentation-study case-time events (ENG-202).
 *
 * The browser records *when* things happened; the backend works out active
 * time from those timestamps (`POST /api/labeling/projects/{id}/case-events`).
 * This module owns the rules that are easy to get wrong and holds no React:
 *
 * - `activity` throttle: leading + trailing in a 15s window. The first input
 *   creates an event at its own time; more input in the same window creates one
 *   more when the window ends, stamped with the *last* input, and opens the next
 *   window. Continuous work → about one event per 15s; idle → none.
 * - Sending: at most 200 events per request, one request at a time. A retry
 *   resends the same event objects, so the backend dedupes them by event_id.
 * - Mirror: the unsent queue is copied to localStorage (per user, per page
 *   session) so a reload or crash resends it on the next page load.
 * - Stamps: `stamp()` builds the `time_event` for endpoints where the backend
 *   writes the event itself (mark complete, annotation save, …).
 *
 * Every stamped or queued event is also kept in a per-study local copy
 * (`knownEvents`) for the pill's live stretch.
 */
import { logger } from "@/utils/logger";

export type ClientCaseEventType =
  | "case_open"
  | "activity"
  | "tab_hidden"
  | "tab_visible"
  | "annotation_start"
  | "annotation_cancel";

/** Written by the backend from a `time_event` stamp in the action's body. */
export type ServerCaseEventType =
  | "mark_complete"
  | "unmark"
  | "mark_unassessable"
  | "clear_unassessable"
  | "annotation_save";

export type CaseTimeEventType = ClientCaseEventType | ServerCaseEventType;

/** One event as sent to `POST /case-events` (extra fields are rejected there). */
export interface CaseEventIn {
  event_id: string;
  type: ClientCaseEventType;
  client_ts: string;
  page_session_id: string;
  study_instance_uid: string;
  annotation_session_id?: string;
  edited_annotation_id?: string;
}

/** `time_event` body field for mark-complete / unmark / unassessable / clear / save. */
export interface TimeEventStamp {
  event_id: string;
  client_ts: string;
  page_session_id: string;
  annotation_session_id?: string;
}

/** An event this page stamped or queued, as the case-time rules read it. */
export interface KnownCaseEvent {
  event_id: string;
  type: CaseTimeEventType;
  client_ts: string;
  page_session_id: string;
}

export type RecordCaseEventsResult =
  | {
      kind: "ok";
      accepted: string[];
      duplicate: string[];
      rejected: { event_id: string | null; index: number; reason?: string }[];
    }
  /** 5xx / 401 / network: keep the events and send them again later. */
  | { kind: "retry" }
  /** 403 / 423 / 400 / 422: this batch can never succeed. */
  | { kind: "drop" }
  /** 404: the project isn't a segmentation study. */
  | { kind: "not_study" };

export type SendCaseEvents = (
  projectId: string,
  events: CaseEventIn[],
  options: { keepalive: boolean },
) => Promise<RecordCaseEventsResult>;

export const ACTIVITY_WINDOW_MS = 15_000;
/** Contract cap per request; also keeps a keepalive body under 64 KB. */
export const MAX_EVENTS_PER_REQUEST = 200;
/** Oldest unsent events are dropped past this, so storage can't fill up. */
export const MAX_QUEUED_EVENTS = 2_000;

const STORAGE_PREFIX = "caseEvents.v1.";

interface StoredSession {
  projectId: string;
  events: CaseEventIn[];
  updatedAt: number;
}

type StoredQueues = Record<string, StoredSession>;

export interface CaseEventQueueOptions {
  projectId: string;
  pageSessionId: string;
  /** Mirror key owner. null → no localStorage mirror (not signed in). */
  userId: string | null;
  send: SendCaseEvents;
  /** Defaults to window.localStorage when available. */
  storage?: Storage | null;
  newId?: () => string;
  now?: () => number;
}

function defaultStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export class CaseEventQueue {
  readonly pageSessionId: string;
  private readonly projectId: string;
  private readonly storageKey: string | null;
  private readonly storage: Storage | null;
  private readonly send: SendCaseEvents;
  private readonly newId: () => string;
  private readonly now: () => number;

  private queue: CaseEventIn[] = [];
  private readonly inFlight = new Set<string>();
  private readonly known = new Map<string, KnownCaseEvent[]>();
  private chain: Promise<void> = Promise.resolve();
  private stopped = false;

  // activity throttle state
  private windowTimer: ReturnType<typeof setTimeout> | null = null;
  private windowStudy: string | null = null;
  private pendingInput: { ts: number; studyUid: string } | null = null;

  constructor(options: CaseEventQueueOptions) {
    this.projectId = options.projectId;
    this.pageSessionId = options.pageSessionId;
    this.send = options.send;
    this.storage = options.storage === undefined ? defaultStorage() : options.storage;
    this.storageKey = options.userId ? `${STORAGE_PREFIX}${options.userId}` : null;
    this.newId = options.newId ?? (() => crypto.randomUUID());
    this.now = options.now ?? Date.now;
  }

  /** True after a 404: the project isn't a segmentation study. */
  get isStopped(): boolean {
    return this.stopped;
  }

  get size(): number {
    return this.queue.length;
  }

  /** Unsent events, oldest first (a copy). */
  pending(): CaseEventIn[] {
    return [...this.queue];
  }

  /** Everything this page stamped or queued for a study, oldest first (a copy). */
  knownEvents(studyUid: string): KnownCaseEvent[] {
    return [...(this.known.get(studyUid) ?? [])];
  }

  /** Record one user input. Creates `activity` per the leading + trailing throttle. */
  input(studyUid: string): void {
    if (this.stopped) return;
    const ts = this.now();
    if (this.windowTimer !== null && this.windowStudy === studyUid) {
      this.pendingInput = { ts, studyUid };
      return;
    }
    // New window (or the case changed mid-window: close the old one first).
    this.settleActivity();
    this.enqueue(this.build("activity", studyUid, ts));
    this.openWindow(studyUid);
  }

  /**
   * Emit the pending trailing `activity` now and close the window. Called at
   * stop points so the last input is in the batch that leaves the page.
   */
  settleActivity(): void {
    if (this.windowTimer !== null) clearTimeout(this.windowTimer);
    this.windowTimer = null;
    this.windowStudy = null;
    const pending = this.pendingInput;
    this.pendingInput = null;
    if (pending && !this.stopped) {
      this.enqueue(this.build("activity", pending.studyUid, pending.ts));
    }
  }

  /**
   * Queue a non-activity event (case_open, tab_*, annotation_*). `at` (ISO)
   * stamps it with an earlier moment the caller already recorded, e.g. the
   * annotation window's start; otherwise now.
   */
  add(
    type: Exclude<ClientCaseEventType, "activity">,
    studyUid: string,
    extra: { annotation_session_id?: string; edited_annotation_id?: string; at?: string } = {},
  ): CaseEventIn | null {
    if (this.stopped) return null;
    if (type === "tab_hidden") this.settleActivity();
    const at = extra.at ? Date.parse(extra.at) : Number.NaN;
    const event = this.build(type, studyUid, Number.isNaN(at) ? this.now() : at, extra);
    this.enqueue(event);
    return event;
  }

  /**
   * Build a `time_event` for an action whose event the backend writes. Not
   * queued; kept in the local copy so the live stretch sees it.
   */
  stamp(
    type: ServerCaseEventType,
    studyUid: string,
    extra: { annotation_session_id?: string } = {},
  ): TimeEventStamp | null {
    if (this.stopped) return null;
    this.settleActivity();
    const stamp: TimeEventStamp = {
      event_id: this.newId(),
      client_ts: new Date(this.now()).toISOString(),
      page_session_id: this.pageSessionId,
      ...(extra.annotation_session_id
        ? { annotation_session_id: extra.annotation_session_id }
        : {}),
    };
    this.remember(studyUid, { ...stamp, type });
    return stamp;
  }

  /** Forget a stamp whose action failed, so the live stretch doesn't use it. */
  discardStamp(studyUid: string, eventId: string): void {
    const list = this.known.get(studyUid);
    if (!list) return;
    this.known.set(
      studyUid,
      list.filter((event) => event.event_id !== eventId),
    );
  }

  /**
   * Send the queue. Normal flushes run one at a time and loop until the queue
   * is empty or a send must be retried later. A keepalive flush (page
   * unloading / tab hidden) doesn't wait: it sends one batch of what isn't
   * already in flight.
   */
  flush(options: { keepalive?: boolean } = {}): Promise<void> {
    if (options.keepalive) return this.sendBatch(true).then(() => undefined);
    this.chain = this.chain
      .then(async () => {
        while (!this.stopped && this.queue.some((e) => !this.inFlight.has(e.event_id))) {
          const sent = await this.sendBatch(false);
          if (!sent) break;
        }
      })
      .catch((err) => logger.warn("[CaseEvents] flush failed:", err));
    return this.chain;
  }

  /** Resend the leftovers of other page sessions of this user (earlier loads). */
  restore(): Promise<void> {
    this.chain = this.chain
      .then(async () => {
        const stored = this.readStored();
        for (const [sessionId, entry] of Object.entries(stored)) {
          if (sessionId === this.pageSessionId) continue;
          await this.resendStored(sessionId, entry);
        }
      })
      .catch((err) => logger.warn("[CaseEvents] restore failed:", err));
    return this.chain;
  }

  /** Stop the throttle timer (unmount). Queued events stay mirrored. */
  dispose(): void {
    if (this.windowTimer !== null) clearTimeout(this.windowTimer);
    this.windowTimer = null;
  }

  // ---------------------------------------------------------------------------

  private build(
    type: ClientCaseEventType,
    studyUid: string,
    ts: number,
    extra: { annotation_session_id?: string; edited_annotation_id?: string } = {},
  ): CaseEventIn {
    return {
      event_id: this.newId(),
      type,
      client_ts: new Date(ts).toISOString(),
      page_session_id: this.pageSessionId,
      study_instance_uid: studyUid,
      ...(extra.annotation_session_id
        ? { annotation_session_id: extra.annotation_session_id }
        : {}),
      ...(extra.edited_annotation_id ? { edited_annotation_id: extra.edited_annotation_id } : {}),
    };
  }

  private openWindow(studyUid: string): void {
    this.windowStudy = studyUid;
    this.windowTimer = setTimeout(() => this.closeWindow(), ACTIVITY_WINDOW_MS);
  }

  private closeWindow(): void {
    this.windowTimer = null;
    const pending = this.pendingInput;
    this.pendingInput = null;
    if (!pending || this.stopped) {
      this.windowStudy = null;
      return;
    }
    // Trailing event, then keep throttling while input continues.
    this.enqueue(this.build("activity", pending.studyUid, pending.ts));
    this.openWindow(pending.studyUid);
  }

  private enqueue(event: CaseEventIn): void {
    this.remember(event.study_instance_uid, event);
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUED_EVENTS) {
      const dropped = this.queue.length - MAX_QUEUED_EVENTS;
      this.queue.splice(0, dropped);
      logger.warn(`[CaseEvents] queue over ${MAX_QUEUED_EVENTS}; dropped ${dropped} oldest`);
    }
    this.writeOwn();
  }

  private remember(studyUid: string, event: KnownCaseEvent): void {
    const list = this.known.get(studyUid);
    const entry: KnownCaseEvent = {
      event_id: event.event_id,
      type: event.type,
      client_ts: event.client_ts,
      page_session_id: event.page_session_id,
    };
    if (list) list.push(entry);
    else this.known.set(studyUid, [entry]);
  }

  /** Returns false when the caller should stop looping (retry later / stopped). */
  private async sendBatch(keepalive: boolean): Promise<boolean> {
    const batch = this.queue
      .filter((event) => !this.inFlight.has(event.event_id))
      .slice(0, MAX_EVENTS_PER_REQUEST);
    if (batch.length === 0 || this.stopped) return false;
    for (const event of batch) this.inFlight.add(event.event_id);
    let result: RecordCaseEventsResult;
    try {
      result = await this.send(this.projectId, batch, { keepalive });
    } catch (err) {
      logger.warn("[CaseEvents] send failed:", err);
      result = { kind: "retry" };
    } finally {
      for (const event of batch) this.inFlight.delete(event.event_id);
    }
    return this.applyResult(batch, result);
  }

  private applyResult(batch: CaseEventIn[], result: RecordCaseEventsResult): boolean {
    switch (result.kind) {
      case "retry":
        return false;
      case "not_study":
        this.stopped = true;
        this.settleActivity();
        this.queue = [];
        this.writeOwn();
        return false;
      case "drop":
        logger.warn(`[CaseEvents] dropped a batch of ${batch.length} events`);
        this.removeSent(batch.map((event) => event.event_id));
        return true;
      case "ok": {
        const done = resolveDone(batch, result);
        this.removeSent(done);
        // A 200 that answers for none of the batch would loop forever.
        return done.size > 0;
      }
    }
  }

  private removeSent(eventIds: Iterable<string>): void {
    const done = new Set(eventIds);
    if (done.size === 0) return;
    this.queue = this.queue.filter((event) => !done.has(event.event_id));
    this.writeOwn();
  }

  private async resendStored(sessionId: string, entry: StoredSession): Promise<void> {
    let remaining = entry.events;
    while (remaining.length > 0) {
      const batch = remaining.slice(0, MAX_EVENTS_PER_REQUEST);
      let result: RecordCaseEventsResult;
      try {
        result = await this.send(entry.projectId, batch, { keepalive: false });
      } catch (err) {
        logger.warn("[CaseEvents] resend failed:", err);
        result = { kind: "retry" };
      }
      if (result.kind === "retry") break;
      if (result.kind === "not_study") {
        remaining = [];
        break;
      }
      if (result.kind === "drop") {
        logger.warn(`[CaseEvents] dropped ${batch.length} restored events`);
      }
      const done =
        result.kind === "ok"
          ? resolveDone(batch, result)
          : new Set(batch.map((event) => event.event_id));
      remaining = remaining.filter((event) => !done.has(event.event_id));
      if (done.size === 0) break;
    }
    this.updateStored((stored) => {
      if (remaining.length === 0) delete stored[sessionId];
      else stored[sessionId] = { ...entry, events: remaining };
    });
  }

  private readStored(): StoredQueues {
    if (!this.storage || !this.storageKey) return {};
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as StoredQueues)
        : {};
    } catch {
      return {};
    }
  }

  private updateStored(change: (stored: StoredQueues) => void): void {
    if (!this.storage || !this.storageKey) return;
    try {
      const stored = this.readStored();
      change(stored);
      if (Object.keys(stored).length === 0) this.storage.removeItem(this.storageKey);
      else this.storage.setItem(this.storageKey, JSON.stringify(stored));
    } catch (err) {
      logger.warn("[CaseEvents] localStorage mirror failed:", err);
    }
  }

  /** Mirror this session's queue (read-merge-write: other tabs keep their entries). */
  private writeOwn(): void {
    this.updateStored((stored) => {
      if (this.queue.length === 0) delete stored[this.pageSessionId];
      else
        stored[this.pageSessionId] = {
          projectId: this.projectId,
          events: this.queue,
          updatedAt: this.now(),
        };
    });
  }
}

/** Every event the server answered for (accepted, duplicate or rejected) is done. */
function resolveDone(
  batch: CaseEventIn[],
  result: Extract<RecordCaseEventsResult, { kind: "ok" }>,
): Set<string> {
  const done = new Set<string>([...result.accepted, ...result.duplicate]);
  for (const rejected of result.rejected) {
    const id = rejected.event_id ?? batch[rejected.index]?.event_id;
    if (id) done.add(id);
    logger.warn(`[CaseEvents] event rejected: ${rejected.reason ?? "unknown reason"}`);
  }
  return done;
}
